import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { X } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import OpsLayout from '../components/OpsLayout';
import opsStatusTone from '../utils/opsStatusTone';
import '../css/GroupReportOps.css';

// 기관별 월간 보고서 운영 화면.
//
// 매월 1일 크론이 기관마다 보고서를 만들어 관리자에게 링크로 보낸다. 이 화면에서
//   · 기관별로 보낼지 말지 켜고 끈다
//   · 이번 달(또는 고른 달)에 어디까지 나갔는지, 실패했으면 왜인지 본다
//   · 미리보기로 지금 데이터의 보고서를 열어 본다
//   · 한 기관만 다시 보내거나, 전 기관을 수동으로 돌린다
//
// ⚠️ 실발송/드라이런은 서버 플래그(GROUP_REPORT_DRY_RUN)가 정한다. 이 화면은 그 값을
//    보여줄 뿐 바꾸지 못한다 — 화면에서 실발송을 우회할 수 있으면 안 된다.
// ⚠️ 메뉴에 없다. /gro 로만 들어온다(기관 관리 /hos 와 같은 이유).

const TITLE = '월간 보고서 발송 관리';

const readRole = () => {
    try {
        const decoded = jwtDecode(localStorage.getItem('token'));
        return decoded?.role || decoded?.user_role || null;
    } catch (error) { return null; }
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const describeError = (error, fallback) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    if (message) return message;
    if (status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (status === 403) return '권한이 없습니다. 최고관리자 계정으로 로그인해 주세요.';
    if (error && !error.response) return '서버에 연결하지 못했습니다.';
    return fallback;
};

const fmtTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const monthLabel = (ym) => { const [y, m] = ym.split('-'); return `${y}년 ${Number(m)}월`; };

// 최근 12개월 선택지. 기본은 지난달(서버가 정함).
const monthOptions = () => {
    const out = [];
    const d = new Date();
    for (let i = 0; i < 12; i += 1) {
        const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
        out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
};

// 발송 상태 코드 → 화면 문구. opsStatusTone 이 이 문구로 배지 색을 정한다.
const STATUS_LABEL = {
    sent: '발송',
    skipped: '건너뜀',
    failed: '실패',
    claimed: '진행중'
};

export default function GroupReportOps() {
    const [role] = useState(readRole);
    const isSuperAdmin = role === 'SUPER_ADMIN';

    const [month, setMonth] = useState('');          // '' = 서버 기본(지난달)
    const [targetMonth, setTargetMonth] = useState(null);
    const [flags, setFlags] = useState(null);
    const [groups, setGroups] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);          // 진행 중인 액션 키
    const [noteDraft, setNoteDraft] = useState({});  // groupId → 메모 입력 중

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [ov, hi] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/group-reports/overview`, { ...authHeaders(), params: month ? { month } : {} }),
                axios.get(`${API_BASE_URL}/api/group-reports/history`, { ...authHeaders(), params: { limit: 60 } })
            ]);
            setTargetMonth(ov.data.targetMonth.slice(0, 7));
            setFlags(ov.data.flags);
            setGroups(ov.data.groups || []);
            setHistory(hi.data.history || []);
        } catch (e) {
            setError(describeError(e, '현황을 불러오지 못했습니다.'));
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

    const toggle = async (g) => {
        setBusy(`t${g.id}`);
        try {
            await axios.put(`${API_BASE_URL}/api/group-reports/settings/${g.id}`,
                { enabled: !g.enabled, note: noteDraft[g.id] ?? g.note ?? '' }, authHeaders());
            setNotice(`${g.name} — 발송 ${!g.enabled ? '켬' : '끔'}`);
            await load();
        } catch (e) { setError(describeError(e, '설정을 저장하지 못했습니다.')); }
        finally { setBusy(null); }
    };

    const saveNote = async (g) => {
        if ((noteDraft[g.id] ?? '') === (g.note ?? '')) return;
        setBusy(`n${g.id}`);
        try {
            await axios.put(`${API_BASE_URL}/api/group-reports/settings/${g.id}`,
                { enabled: g.enabled, note: noteDraft[g.id] ?? '' }, authHeaders());
            await load();
        } catch (e) { setError(describeError(e, '메모를 저장하지 못했습니다.')); }
        finally { setBusy(null); }
    };

    const sendOne = async (g, force) => {
        const what = force ? '다시 보내기' : '보내기';
        const mode = flags?.dryRun ? '드라이런(파일만 생성, 메일 없음)' : '⚠️ 실제 메일 발송';
        if (!window.confirm(`${g.name} · ${monthLabel(targetMonth)} 보고서를 ${what}합니다.\n모드: ${mode}\n계속할까요?`)) return;
        setBusy(`s${g.id}`);
        try {
            const r = await axios.post(`${API_BASE_URL}/api/group-reports/send/${g.id}`,
                { month: targetMonth, force: !!force }, authHeaders());
            setNotice(`${g.name} — ${STATUS_LABEL[r.data.result] ?? r.data.result}${r.data.dryRun ? ' (드라이런)' : ''}`);
            await load();
        } catch (e) { setError(describeError(e, '발송하지 못했습니다.')); }
        finally { setBusy(null); }
    };

    const runAll = async () => {
        const mode = flags?.dryRun ? '드라이런(파일만 생성, 메일 없음)' : '⚠️ 실제 메일 발송';
        if (!window.confirm(`${monthLabel(targetMonth)} 보고서를 켜진 전 기관에 보냅니다.\n이미 보낸 기관은 건너뜁니다.\n모드: ${mode}\n계속할까요?`)) return;
        setBusy('all');
        try {
            const r = await axios.post(`${API_BASE_URL}/api/group-reports/run`, { month: targetMonth }, authHeaders());
            const t = r.data.tally;
            setNotice(`전체 실행 — 발송 ${t.sent} · 건너뜀 ${t.skipped} · 실패 ${t.failed} · 이미 처리됨 ${t.claimedElsewhere}${r.data.dryRun ? ' (드라이런)' : ''}`);
            await load();
        } catch (e) { setError(describeError(e, '전체 실행에 실패했습니다.')); }
        finally { setBusy(null); }
    };

    const preview = (g) => {
        // 새 탭. 토큰은 헤더로만 가야 해서 URL 에 못 싣는다 → fetch 로 받아 blob 으로 연다.
        setBusy(`p${g.id}`);
        axios.get(`${API_BASE_URL}/api/group-reports/preview/${g.id}`, { ...authHeaders(), params: { month: targetMonth }, responseType: 'blob' })
            .then((r) => { window.open(URL.createObjectURL(new Blob([r.data], { type: 'text/html' })), '_blank'); })
            .catch((e) => setError(describeError(e, '미리보기를 만들지 못했습니다.')))
            .finally(() => setBusy(null));
    };

    if (!isSuperAdmin) {
        return (
            <OpsLayout sideNav={false} title={TITLE}>
                <div className="sds-notice sds-notice--error">
                    이 화면은 최고관리자만 사용할 수 있습니다.
                </div>
            </OpsLayout>
        );
    }

    const enabledCount = groups.filter((g) => g.enabled).length;
    const sentCount = groups.filter((g) => g.send?.status === 'sent').length;
    const failedCount = groups.filter((g) => g.send?.status === 'failed').length;

    const description = targetMonth
        ? `${monthLabel(targetMonth)} 기준 · 기관 ${groups.length} · 발송 켬 ${enabledCount} · 발송됨 ${sentCount}${failedCount > 0 ? ` · 실패 ${failedCount}` : ''}`
        : undefined;

    // 서버 환경변수가 정하는 운영 모드. 상태가 아니라 모드라 opsStatusTone 대신 직접 매핑한다.
    let modeLabel = null;
    let modeTone = 'none';
    if (flags) {
        if (!flags.enabled) { modeLabel = '크론 꺼짐'; modeTone = 'none'; }
        else if (flags.dryRun) { modeLabel = '드라이런 모드'; modeTone = 'caution'; }
        else { modeLabel = '실발송 모드'; modeTone = 'info'; }
    }

    return (
        <OpsLayout
            sideNav={false}
            title={TITLE}
            description={description}
            actions={
                <>
                    {modeLabel && (
                        <span
                            className={`sds-badge sds-badge--${modeTone}`}
                            title="서버 환경변수(GROUP_REPORT_ENABLED / GROUP_REPORT_DRY_RUN)가 정합니다. 화면에서 바꿀 수 없습니다."
                        >
                            {modeLabel}
                        </span>
                    )}
                    <label className="groupreport-page__month">
                        대상 월
                        <select className="sds-select" value={month} onChange={(e) => setMonth(e.target.value)}>
                            <option value="">지난달 (기본)</option>
                            {monthOptions().map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
                        </select>
                    </label>
                    <button className="sds-btn sds-btn--primary" disabled={!!busy || loading} onClick={runAll}>
                        {busy === 'all' ? '실행 중…' : `전 기관 ${flags?.dryRun ? '생성' : '발송'}`}
                    </button>
                </>
            }
        >
            <section className="ops-page__card groupreport-page">
                {notice && (
                    <div className="sds-notice">
                        {notice}
                        <span className="sds-toolbar__spacer" />
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setNotice(null)} aria-label="닫기">
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                )}
                {error && (
                    <div className="sds-notice sds-notice--error">
                        {error}
                        <span className="sds-toolbar__spacer" />
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={load}>다시 시도</button>
                    </div>
                )}

                {loading ? (
                    <div className="sds-notice">불러오는 중…</div>
                ) : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th>기관</th>
                                    <th className="sds-table__center">소속</th>
                                    <th className="sds-table__center">수신자</th>
                                    <th className="sds-table__center">발송</th>
                                    <th>메모</th>
                                    <th>상태</th>
                                    <th>처리 시각</th>
                                    <th className="sds-table__center">열람</th>
                                    <th className="sds-table__center">작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => {
                                    const send = g.send;
                                    const statusLabel = send ? (STATUS_LABEL[send.status] || send.status) : '미발송';
                                    const tone = opsStatusTone(statusLabel);
                                    const warn = g.recipientCount === 0 ? '수신자 없음' : g.memberCount === 0 ? '소속자 없음' : null;
                                    return (
                                        <tr key={g.id} className={g.enabled ? '' : 'groupreport-page__row--off'}>
                                            <td className="groupreport-page__name">
                                                {g.name}
                                                <span className="groupreport-page__id">{g.id}</span>
                                            </td>
                                            <td className="sds-table__center">{g.memberCount}</td>
                                            <td className={`sds-table__center${g.recipientCount === 0 ? ' groupreport-page__num--danger' : ''}`}>
                                                {g.recipientCount}
                                            </td>
                                            <td className="sds-table__center">
                                                <button
                                                    role="switch"
                                                    aria-checked={g.enabled}
                                                    className="groupreport-page__toggle"
                                                    disabled={busy === `t${g.id}`}
                                                    onClick={() => toggle(g)}
                                                    title={g.enabled ? '끄기' : '켜기'}
                                                >
                                                    <span className="groupreport-page__toggle-knob" aria-hidden="true" />
                                                </button>
                                            </td>
                                            <td>
                                                <input
                                                    className="sds-input sds-input--sm groupreport-page__note"
                                                    maxLength={500}
                                                    placeholder={warn || ''}
                                                    value={noteDraft[g.id] ?? g.note ?? ''}
                                                    onChange={(e) => setNoteDraft({ ...noteDraft, [g.id]: e.target.value })}
                                                    onBlur={() => saveNote(g)}
                                                />
                                            </td>
                                            <td>
                                                <span className={`sds-badge sds-badge--${tone}`} title={send?.error || ''}>
                                                    {statusLabel}{send?.dryRun ? ' (드라이런)' : ''}
                                                </span>
                                                {send?.error && <div className="groupreport-page__error">{send.error}</div>}
                                            </td>
                                            <td className="sds-table__muted">{fmtTime(send?.finishedAt || send?.claimedAt)}</td>
                                            <td className="sds-table__center">
                                                {g.link ? (
                                                    <span title={`마지막 열람 ${fmtTime(g.link.lastViewedAt)} · 만료 ${fmtTime(g.link.expiresAt)}`}>
                                                        {g.link.viewCount}회
                                                    </span>
                                                ) : '-'}
                                            </td>
                                            <td className="sds-table__center">
                                                <div className="sds-row-actions">
                                                    <button className="sds-btn sds-btn--ghost sds-btn--sm" disabled={!!busy} onClick={() => preview(g)}>
                                                        {busy === `p${g.id}` ? '…' : '미리보기'}
                                                    </button>
                                                    {send
                                                        ? <button className="sds-btn sds-btn--weak sds-btn--sm" disabled={!!busy || !g.enabled} onClick={() => sendOne(g, true)}>다시 보내기</button>
                                                        : <button className="sds-btn sds-btn--weak sds-btn--sm" disabled={!!busy || !g.enabled} onClick={() => sendOne(g, false)}>보내기</button>}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="ops-page__card groupreport-page__history">
                <div className="ops-page__card-title"><h2>최근 발송 이력</h2></div>
                <div className="sds-table-wrap">
                    <table className="sds-table">
                        <thead>
                            <tr>
                                <th>시각</th><th>대상 월</th><th>기관</th><th>상태</th>
                                <th className="sds-table__center">사용자</th>
                                <th className="sds-table__center">수신자</th>
                                <th>인스턴스</th><th>오류</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h) => {
                                const statusLabel = STATUS_LABEL[h.status] || h.status;
                                const tone = opsStatusTone(statusLabel);
                                return (
                                    <tr key={h.id}>
                                        <td>{fmtTime(h.finishedAt || h.claimedAt)}</td>
                                        <td>{h.targetMonth ? monthLabel(h.targetMonth.slice(0, 7)) : '-'}</td>
                                        <td>{h.groupName || h.groupId}</td>
                                        <td>
                                            <span className={`sds-badge sds-badge--${tone}`}>
                                                {statusLabel}{h.dryRun ? ' (드라이런)' : ''}
                                            </span>
                                        </td>
                                        <td className="sds-table__center">{h.memberCount ?? '-'}</td>
                                        <td className="sds-table__center groupreport-page__recip" title={h.recipients.join(', ')}>
                                            {h.recipients.length}
                                        </td>
                                        <td className="groupreport-page__host">{h.claimedBy || '-'}</td>
                                        <td className="groupreport-page__error">{h.error || ''}</td>
                                    </tr>
                                );
                            })}
                            {history.length === 0 && (
                                <tr><td colSpan={8} className="sds-empty">아직 발송 이력이 없습니다.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </OpsLayout>
    );
}
